import React, { useState, useEffect, useRef, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchFeeScheduleProviderDetails,
  fetchPreferredNetworks,
  fetchFeeSchedules,
  fetchFeeScheduleFormDetails,
} from "../../../store/Practice/FeeSchedule/feeScheduleActions";
import PopupModal from "../../../common/PopupModal";
import "./FeeSchedule.scss";
import { formatDate } from "../../../utils/commonUtilities";
import { useLocation } from "react-router-dom";
import defaultProfileIcon from "../../../assets/images/defaultProfileIcon.svg";
import { MyAccountContext } from "../../../App";

const FeeSchedules = () => {
  const location = useLocation();
  const locationData = location.pathname.split("/");
  const PRACTICE_ID = locationData[2];
  const { grantedRoles = [] } = useContext(MyAccountContext) || {};
  const isManager = grantedRoles.includes("ROLE_MANAGER");
  const [providerId, setProviderId] = useState("");
  const [openPopup, setOpenPopup] = useState(false);
  const [activeCategory, setActiveCategory] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [selectedFeeSchedule, setSelectedFeeSchedule] = useState("");
  const [filter, setFilter] = useState("ACTIVE");
  const [showError, setShowError] = useState(false);

  const apiCallsInitiated = useRef(false);

  const dispatch = useDispatch();

  const {
    isLoadingProviderDetails,
    feeScheduleProviderDetails,
    errorProviderDetails,
    errorProviderDetailsMsg,
  } = useSelector((state) => state.feeScheduleProviderDetails);

  const {
    isLoadingPreferredNetworks,
    preferredNetworks,
    errorPreferredNetworks,
    errorPreferredNetworksMsg,
  } = useSelector((state) => state.preferredNetworks);

  const {
    isLoadingFeeSchedules,
    feeSchedules,
    errorFeeSchedules,
    errorFeeSchedulesMsg,
  } = useSelector((state) => state.feeSchedules);

  const {
    isLoadingFormDetails,
    feeScheduleFormDetails,
    errorFormDetails,
    errorFormDetailsMsg,
    formDetailsDeleteError,
  } = useSelector((state) => state.feeScheduleFormDetails);

  const providersArrayData =
      feeScheduleProviderDetails.data?.accessPointBriefResponseDTO.items || [];
    const providersArray = providersArrayData
      .filter((item) => item?.status.isActive === "true")
      .sort((a, b) => {
        const lastA = (a?.lastName || '').toLowerCase();
        const lastB = (b?.lastName || '').toLowerCase();
        if (lastA < lastB) return -1;
        if (lastA > lastB) return 1;
        return 0;
      });

  useEffect(() => {
    if (!apiCallsInitiated.current) {
      apiCallsInitiated.current = true;
      dispatch(fetchFeeScheduleProviderDetails(PRACTICE_ID));
    }
  }, []);

  useEffect(() => {
    if (providerId) {
      dispatch(
        fetchPreferredNetworks({
          practiceId: PRACTICE_ID,
          providerId: providerId,
        })
      );

      dispatch(
        fetchFeeSchedules({
          practiceId: PRACTICE_ID,
          providerId: providerId,
        })
      );
    }
  }, [providerId]);

  useEffect(() => {
    if (
      errorProviderDetails ||
      errorPreferredNetworks ||
      errorFeeSchedules ||
      errorFormDetails
    ) {
      setShowError(true);
      if (errorFormDetails) {
        setOpenPopup(false);
      }

      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  }, [
    errorProviderDetails,
    errorPreferredNetworks,
    errorFeeSchedules,
    errorFormDetails,
  ]);

  useEffect(() => {
    if (
      feeScheduleProviderDetails?.data?.accessPointBriefResponseDTO?.items
        ?.length > 0
    ) {
      setProviderId(
      providersArray[0]?.providerDisplayId
        )
      
    }
    const procedureCategories =
      feeScheduleFormDetails?.data?.accessPointFeeScheduleCarrierResponseDTO
        ?.procedureCategories;
    if (procedureCategories && procedureCategories.length > 0) {
      setActiveCategory(procedureCategories[0].procedureCategory);
    }
  }, [feeScheduleProviderDetails, feeScheduleFormDetails]);

  const handleTabClick = (providerDisplayId) => {
    setProviderId(providerDisplayId);
  };

  const handleViewClick = (network, feeSchedule, feeScheduleId) => {
    setSelectedNetwork(network);
    setSelectedFeeSchedule(feeSchedule);
    setOpenPopup(true);

    if (feeScheduleId) {
      dispatch(
        fetchFeeScheduleFormDetails({
          practiceId: PRACTICE_ID,
          providerId: providerId,
          feeScheduleId: feeScheduleId,
        })
      )
        .unwrap()
        .catch(() => {
          setOpenPopup(false);
        });
    }
  };

  const RenderNavTabs = () => {
    if (isLoadingProviderDetails) return <div>Loading providers...</div>;

    if (
      !feeScheduleProviderDetails ||
      !feeScheduleProviderDetails.data ||
      !feeScheduleProviderDetails.data.accessPointBriefResponseDTO ||
      !feeScheduleProviderDetails.data.accessPointBriefResponseDTO.items
    ) { 
      return (
        <div className="full-width-container display-flex">
          <div className="col-md-12">
            <div className="feeSchedule-section">
              <p className="feeSchedule-module">
                No provider details available
              </p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="full-width-container display-flex">
        <div className="col-md-12">
          <nav className="tab-wrapper">
            <ul className="nav nav-tabs" role="tablist">
              {providersArray.map((item, index) => (
                <li
                  key={index}
                  onClick={() => handleTabClick(item.providerDisplayId)}
                  className={
                    providerId === item.providerDisplayId ? "active" : ""
                  }
                >
                  <img
                    className={`provider-image${
                      providerId === item.providerDisplayId ? "" : " grayscale"
                    }`}
                    src={
                      item.profileImage.small
                        ? item.profileImage.small
                        : defaultProfileIcon
                    }
                  />
                  <div className="provider-name">
                    Dr. {item.firstName ? item.firstName.charAt(0) : ""}{" "}
                    {item.lastName || ""}
                  </div>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    );
  };

  const RenderPreferredNetwork = () => {
    if (isLoadingPreferredNetworks)
      return <div>Loading preferred networks...</div>;

    if (!preferredNetworks?.data) {
      return (
        <div className="full-width-container display-flex">
          <div className="col-md-12">
            <section id="fee-schedule" role="tabpanel">
              <h3 className="section-header-networks">Preferred Networks</h3>
              <div className="feeSchedule-section">
                <p className="feeSchedule-module">
                  No preferred networks available
                </p>
              </div>
            </section>
          </div>
        </div>
      );
    }

    if (
      !preferredNetworks.data.accessPointPreferredNetworkAccessResponseDTO ||
      preferredNetworks.data.accessPointPreferredNetworkAccessResponseDTO
        .length === 0
    ) {
      return (
        <div className="full-width-container display-flex">
          <div className="col-md-12">
            <section id="fee-schedule" role="tabpanel">
              <h3 className="section-header-networks">Preferred Networks</h3>
              <div className="feeSchedule-section">
                <p className="feeSchedule-module">No preferred networks.</p>
              </div>
            </section>
          </div>
        </div>
      );
    }

    return (
      <div className="full-width-container display-flex">
        <div className="col-md-12">
          <section id="fee-schedule" role="tabpanel">
            <h3 className="section-header-networks">Preferred Networks</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Network Access</th>
                  <th>Preferred Carrier/Network</th>
                  <th>Fee Schedule</th>
                </tr>
              </thead>
              <tbody>
                {preferredNetworks.data.accessPointPreferredNetworkAccessResponseDTO.map(
                  (item, index) => (
                    <tr key={index}>
                      <td>{item?.networkAccess}</td>
                      <td>{item?.preferredNetwork}</td>
                      <td>{item?.feeSchedule}</td>
                      {!isManager && (
                        <td className="view-btn">
                          <button
                            className="btn btn-white pill"
                            onClick={() =>
                              handleViewClick(
                                item?.networkAccess,
                                item.feeSchedule,
                                null
                              )
                            }
                          >
                            View
                          </button>
                        </td>
                      )}
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </section>
        </div>
      </div>
    );
  };

  const RenderFeeSchedule = () => {
    if (isLoadingFeeSchedules) return <div>Loading Fee Schedules...</div>;

    if (!feeSchedules?.data) {
      return (
        <div className="full-width-container display-flex">
          <div className="col-md-12">
            <section>
              <h3 className="section-header-networks">Fee Schedules</h3>
              <div className="feeSchedule-section">
                <p className="feeSchedule-module">No fee schedules.</p>
              </div>
            </section>
          </div>
        </div>
      );
    }

    const currentDate = new Date();
    const isActive = (startDate, endDate) => {
      const start = new Date(startDate);
      const end = endDate ? new Date(endDate) : null;
      return (!end || end >= currentDate) && start <= currentDate;
    };

    const feeSchedulesData =
      feeSchedules.data.accessPointFeeScheduleCarrierResponseDTO || [];

    const filteredSchedules = feeSchedulesData.filter((carrier) => {
      return carrier.networks.some((network) => {
        if (filter === "ALL") return true;
        const active = isActive(network.startDate, network.endDate);
        return (
          (filter === "ACTIVE" && active) || (filter === "INACTIVE" && !active)
        );
      });
    });

    if (filteredSchedules.length === 0) {
      return (
        <div className="full-width-container display-flex">
          <div className="col-md-12">
            <section>
              <div className="section-header-networks">
                Select a Fee Schedule
                <div className="filter-buttons">
                  <span
                    onClick={() => setFilter("ALL")}
                    className={filter === "ALL" ? "filter-text" : ""}
                  >
                    ALL
                  </span>
                  <span className="textRightBorder">|</span>
                  <span
                    onClick={() => setFilter("ACTIVE")}
                    className={filter === "ACTIVE" ? "filter-text" : ""}
                  >
                    ACTIVE
                  </span>
                  <span className="textRightBorder">|</span>
                  <span
                    onClick={() => setFilter("INACTIVE")}
                    className={filter === "INACTIVE" ? "filter-text" : ""}
                  >
                    INACTIVE
                  </span>
                </div>
              </div>
              <div className="feeSchedule-section">
                <p className="feeSchedule-module">No fee schedules.</p>
              </div>
            </section>
          </div>
        </div>
      );
    }

    return (
      <div className="full-width-container display-flex">
        <div className="col-md-12">
          <section>
            <div className="section-header-networks">
              Select a Fee Schedule
              <div className="filter-buttons">
                <span
                  onClick={() => setFilter("ALL")}
                  className={filter === "ALL" ? "filter-text" : ""}
                >
                  ALL
                </span>
                <span className="textRightBorder">|</span>
                <span
                  onClick={() => setFilter("ACTIVE")}
                  className={filter === "ACTIVE" ? "filter-text" : ""}
                >
                  ACTIVE
                </span>
                <span className="textRightBorder">|</span>
                <span
                  onClick={() => setFilter("INACTIVE")}
                  className={filter === "INACTIVE" ? "filter-text" : ""}
                >
                  INACTIVE
                </span>
              </div>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Network</th>
                  <th>Fee Schedule</th>
                  <th>Effective Date</th>
                  <th>End Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredSchedules.map((carrier, carrierIndex) => (
                  <React.Fragment key={carrierIndex}>
                    <tr className="carrierName">
                      <td colSpan="5">{carrier.carrierName}</td>
                    </tr>
                    {carrier.networks
                      .filter((network) => {
                        if (filter === "ALL") return true;
                        const active = isActive(
                          network.startDate,
                          network.endDate
                        );
                        return (
                          (filter === "ACTIVE" && active) ||
                          (filter === "INACTIVE" && !active)
                        );
                      })
                      .map((network, networkIndex) => (
                        <tr key={`${carrierIndex}-${networkIndex}`}>
                          <td>{network.networkName}</td>
                          <td>{network.feeScheduleName}</td>
                          <td>{formatDate(network.startDate)}</td>
                          <td>
                            {network.endDate
                              ? formatDate(network.endDate)
                              : "-"}
                          </td>
                          {!isManager && (
                            <td className="view-btn">
                              <button
                                className="btn btn-white pill"
                                onClick={() =>
                                  handleViewClick(
                                    network.networkName,
                                    network.feeScheduleName,
                                    network.feeScheduleDisplayId
                                  )
                                }
                              >
                                View
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      </div>
    );
  };

  const RenderFeeScheduleForm = ({ feeSchedule, onClose }) => {
    if (isLoadingFormDetails) {
      return (
        <div className="loading-container">Loading fee schedule details...</div>
      );
    }

    if (!feeScheduleFormDetails?.data) {
      return (
        <div className="loading-container">
          No fee schedule details available
        </div>
      );
    }

    const { data } = feeScheduleFormDetails;
    const procedureCategoriesData =
      data.accessPointFeeScheduleCarrierResponseDTO?.procedureCategories;
    const procedureCategories = Array.isArray(procedureCategoriesData)
      ? procedureCategoriesData
      : procedureCategoriesData
      ? [procedureCategoriesData]
      : [];

    if (procedureCategories.length === 0) {
      return (
        <div className="loading-container">
          No procedure categories available
        </div>
      );
    }

    return (
      <div className="row schedule-container">
        <nav className="col-3 left-side-block">
          <div className="goBack">
            <a onClick={onClose}>
              <i className="bi bi-chevron-left"></i> Go Back{" "}
            </a>
          </div>

          <div>
            <div className="feeScheduleText">FEE SCHEDULE</div>
            <div className="feeScheduleCode">
              {selectedNetwork} {feeSchedule}
            </div>
          </div>
          <ul className="nav flex-column leftSideNavBar">
            <li className="nav-item">
              <a
                href="#"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveCategory(
                    feeScheduleFormDetails[0]?.procedureCategories?.[0]
                      ?.procedureCategory || "All Procedures"
                  );
                }}
              >
                All Procedures
              </a>
            </li>
            {procedureCategories.map((category, index) => (
              <li key={index} className="nav-item">
                <a
                  href="#"
                  className={`nav-link ${
                    activeCategory === category.procedureCategory
                      ? "active"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveCategory(category.procedureCategory);
                  }}
                >
                  {category.procedureCategory}
                  {activeCategory === category.procedureCategory && (
                    <i className="bi bi-caret-right-fill"></i>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <div className="rightSiderBar col-9">
          <table className="table">
            <thead>
              <tr>
                <th>Performed</th>
                <th>Code</th>
                <th>Procedure</th>
                <th>Prices</th>
                <th>Disc%</th>
              </tr>
            </thead>
            <tbody>
              {procedureCategories.map(
                (category) =>
                  (activeCategory === "All Procedures" ||
                    category.procedureCategory === activeCategory) && (
                    <React.Fragment key={category.procedureCategory}>
                      <tr>
                        <td className="procedureCategory" colSpan="5">
                          <strong>{category.procedureCategory}</strong>
                        </td>
                      </tr>
                      {category.procedureSubCategories &&
                        Array.isArray(category.procedureSubCategories) &&
                        category.procedureSubCategories.map(
                          (subCategory, index) => (
                            <React.Fragment key={index}>
                              <tr>
                                <td
                                  className="procedureSubCategory"
                                  colSpan="5"
                                >
                                  <strong>
                                    {subCategory.procedureSubCategory}
                                  </strong>
                                </td>
                              </tr>
                              {Array.isArray(subCategory.procedures) &&
                                subCategory.procedures.map(
                                  (procedure, index) => (
                                    <tr key={index}>
                                      <td>
                                        {procedure.iProvided && (
                                          <span className="green-circle">
                                            <i className="bi bi-check-circle"></i>
                                          </span>
                                        )}
                                      </td>
                                      <td>{procedure.adaCode}</td>
                                      <td>{procedure.description}</td>
                                      <td>{procedure.fee}</td>
                                      <td>{procedure.discountPercent || ""}</td>
                                    </tr>
                                  )
                                )}
                            </React.Fragment>
                          )
                        )}
                    </React.Fragment>
                  )
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const RenderPopupModal = () => {
    return (
      <PopupModal
        isOpen={openPopup}
        onClose={() => setOpenPopup(false)}
        popupHeading="FEE SCHEDULE"
        customRender={() => (
          <RenderFeeScheduleForm
            feeSchedule={selectedFeeSchedule}
            onClose={() => setOpenPopup(false)}
          />
        )}
      />
    );
  };

  const RenderErrors = () => {
    if (!showError) return null;

    let errorMessage = "";

    if (errorProviderDetails && errorProviderDetailsMsg)
      errorMessage = errorProviderDetailsMsg;
    else if (errorPreferredNetworks && errorPreferredNetworksMsg)
      errorMessage = errorPreferredNetworksMsg;
    else if (errorFeeSchedules && errorFeeSchedulesMsg)
      errorMessage = errorFeeSchedulesMsg;
    else if (errorFormDetails && errorFormDetailsMsg)
      errorMessage = errorFormDetailsMsg;
    else if (formDetailsDeleteError) errorMessage = formDetailsDeleteError;

    if (!errorMessage) return null;

    return (
      <div className="overlay-alert alert alert-danger" role="alert">
        {errorMessage || "An error occurred. Please try again."}
      </div>
    );
  };

  if (
    !feeScheduleProviderDetails?.data?.accessPointBriefResponseDTO?.items
      ?.length
  ) {
    return null;
  }

  return (
    <>
      <div className="pt-4 fee-schedule-wrapper">
        {RenderErrors()}
        {RenderNavTabs()}
        {RenderPreferredNetwork()}
        {RenderFeeSchedule()}
        {RenderPopupModal()}
      </div>
    </>
  );
};

export default FeeSchedules;
